/**
 * The small types everything else is built from.
 *
 * Defined once here and imported everywhere, so the API, the website's registration island, the
 * management app and eventually the native app all agree on what a valid ULID or a valid
 * calendar date is. A schema redeclared in an app is the beginning of the two ends of a form
 * disagreeing about what it accepts.
 */

import { z } from "zod";

/**
 * Crockford base32, 26 characters. ULIDs rather than UUIDs because they sort by creation time,
 * which matters for rows a judge's device generates offline and syncs later — the natural
 * ordering is the order they were entered in.
 */
export const ulidSchema = z
  .string()
  .length(26)
  .regex(/^[0-7][0-9A-HJKMNP-TV-Z]{25}$/, "Not a ULID");

export type Ulid = z.infer<typeof ulidSchema>;

/** A URL-safe identifier for a community or an event. Lowercase, no leading or trailing dash. */
export const slugSchema = z
  .string()
  .min(2)
  .max(63)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and single dashes");

export const hostnameSchema = z
  .string()
  .min(3)
  .max(253)
  .toLowerCase()
  .regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/, "Not a hostname");

/** `YYYY-MM-DD`, and a real date — the 30th of February is rejected here, not at the database. */
export const plainDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD")
  .refine((value) => {
    const [y, m, d] = value.split("-").map(Number) as [number, number, number];
    if (m < 1 || m > 12 || d < 1) return false;
    return d <= new Date(Date.UTC(y, m, 0)).getUTCDate();
  }, "That date does not exist");

/** `HH:MM`, 24-hour. A wall-clock time with no date and no zone. */
export const plainTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:MM on a 24-hour clock");

/** UTC epoch milliseconds. */
export const instantSchema = z.number().int().finite();

/**
 * An IANA zone, checked against what the runtime's ICU data actually knows rather than against a
 * pattern. A zone that formats nowhere is worse than a rejected one.
 */
export const timezoneSchema = z.string().refine((value) => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}, "Unknown timezone");

/** Integer cents. Never a float, and never negative — a refund is a void, not a minus sign. */
export const centsSchema = z.number().int().min(0);

export const currencySchema = z.string().length(3).toUpperCase();

/** Free text a person typed. Trimmed, bounded, and never empty when required. */
export const shortTextSchema = z.string().trim().min(1).max(200);
export const longTextSchema = z.string().trim().max(10_000);

/**
 * The justification a workflow demands before it will touch money or rank.
 *
 * Deliberately requires more than a keystroke: this is the friction half of "speed where volume
 * lives, friction where money and fairness live" (ux.md §2), and a reason of "x" would satisfy
 * the letter of the audit trail while defeating its purpose.
 */
export const reasonSchema = z.string().trim().min(4, "Say why, in a few words").max(2000);

/**
 * A phone number as a person wrote it. Not normalised to E.164 — the pilot community's contacts
 * are all phone numbers written in local form, and rejecting or rewriting what someone typed at
 * a registration desk costs more than the tidiness gains (D21).
 */
export const phoneSchema = z.string().trim().min(7).max(32);

export const emailSchema = z.email().max(254);

/** Optional in the schema sense *and* tolerant of the empty string a form control submits. */
export function optionalText<T extends z.ZodType<string>>(schema: T) {
  return z.preprocess(
    (value) => (value === "" || value === null ? undefined : value),
    schema.optional(),
  );
}

/**
 * A tenant-supplied URL, narrowed to something safe to put in an `href` or an `img src`.
 *
 * `z.url()` accepts any scheme the URL parser does, which includes `javascript:` and `data:` —
 * schemes that escaping does nothing about, and that a self-serve branding screen would otherwise
 * let a community aim at its own visitors. Anything that is not `http` or `https` comes back
 * `undefined`, so a caller renders nothing rather than something broken.
 *
 * Lives here, beside the schemas, because both ends need it: the settings screen validates on the
 * way in, and every surface that reads `branding_json` back out — the public site, the staff
 * header — is reading rows that may predate the validation.
 *
 * A pattern rather than `new URL`, because this package is imported by the native app and React
 * Native's `URL` is a partial implementation — a guard that quietly behaves differently on a
 * phone is worse than no guard. The pattern is deliberately strict: a scheme we allow, then
 * characters that can only be part of a URL. Whitespace and quotes are what a payload smuggled
 * into an attribute needs, so anything carrying them is refused rather than escaped and hoped for.
 */
export function safeUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return /^https?:\/\/[^\s"'<>\\^`{|}]+$/i.test(trimmed) ? trimmed : undefined;
}

/**
 * `/media/{ulid}` — an image this platform stores, named the way both surfaces serve it.
 *
 * The form is a path and not a URL, and that is the whole decision. An uploaded logo is one row
 * read by the public site, the staff app, and the venue hub sitting on a laptop in a field with no
 * route to the internet (D86); an absolute `https://api.pow-wow.ca/...` baked into `branding_json`
 * would be correct on exactly one of those and a broken image on the other two. A path lets each
 * surface resolve it against the origin it already has — the site through its own `/media/{id}`
 * proxy, so the picture is same-origin with the page that shows it, and the staff app against its
 * API base.
 *
 * The two callers that genuinely need an absolute URL — an `og:image` and anything that leaves in
 * an email — resolve it themselves at the point of use, where the origin is known.
 */
const MEDIA_PATH = /^\/media\/[0-9A-HJKMNP-TV-Z]{26}$/i;

export function isMediaPath(value: unknown): boolean {
  return typeof value === "string" && MEDIA_PATH.test(value.trim());
}

/**
 * The same guard as `safeUrl`, widened to the images we host ourselves.
 *
 * Separate from `safeUrl` rather than folded into it, because the two answer different questions: a
 * community's website becomes an `<a href>` and has no business being a local path, while a logo
 * became one the day uploads existed. Anything that is not one of the two forms comes back
 * `undefined` and is rendered as nothing.
 */
export function safeImageSrc(value: unknown): string | undefined {
  if (isMediaPath(value)) return (value as string).trim();
  return safeUrl(value);
}

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(200).optional(),
});

export type Pagination = z.infer<typeof paginationSchema>;
